import React, { useState } from 'react';
import BallsDisplay from '../../../LottoAssistant/components/BallsDisplay';
import BetStatusBadge from '../../../LottoAssistant/components/BetStatusBadge';
import { renderLotteryLogo } from '../../../LottoAssistant/utils/renderLotteryLogo';
import { formatLottoDate } from '../../../LottoAssistant/utils/date';
import {
  isBallBasedMarket,
  formatOdds,
} from '../../../LottoAssistant/utils/common';
import { formatCategory } from '../../../LottoAssistant/utils/marketTypeHelpers';
import { formatMoney } from '../../utils';
import classes from './SubscriptionCard.module.scss';

const ArrowIcon = () => (
  <svg
    width="7"
    height="13"
    viewBox="0 0 7 13"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fill="currentColor"
      d="M0.970131 12.5C1.23775 12.5 1.46356 12.4079 1.64755 12.2238L6.65711 7.26151C6.89128 7.03556 7 6.79289 7 6.5C7 6.20711 6.89128 5.95607 6.66547 5.73849L1.64755 0.776151C1.46356 0.59205 1.23775 0.5 0.970131 0.5C0.434886 0.5 0 0.926778 0 1.46234C0 1.73013 0.108722 1.9728 0.301075 2.17364L4.70012 6.50837L0.301075 10.8347C0.108722 11.0272 0 11.2699 0 11.5377C0 12.0732 0.434886 12.5 0.970131 12.5Z"
    />
  </svg>
);

const SubscriptionCard = ({
  subscription,
  onCancel,
  isCancelling,
  cancelError,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const sequenceBets = [...(subscription.sequence_bets ?? [])].sort(
    (a, b) => (a.sequence_number ?? 0) - (b.sequence_number ?? 0)
  );

  const firstBet = sequenceBets[0];
  const isBallBased = firstBet
    ? isBallBasedMarket(firstBet.outcome?.outcome_type)
    : true;

  const categoryLabel = isBallBased
    ? `${formatCategory(
        subscription.sequence_bets?.[0]?.market_category ?? ''
      )} · ${formatCategory(subscription.sequence_bets?.[0]?.market_title ?? '')}`
    : `${formatCategory(firstBet?.market_title ?? '')} · ${
        firstBet?.outcome?.outcome_title ?? ''
      }`;

  const handleCancel = () => {
    if (!subscription.sequence_id) return;
    onCancel?.(subscription.sequence_id);
  };

  return (
    <div className={classes.Card}>
      <div
        className={classes.ClickableHeader}
        onClick={() => setIsExpanded((prev) => !prev)}
      >
        <div className={classes.Logo}>
          {renderLotteryLogo(subscription.lottery_id)}
        </div>
        <div className={classes.HeaderInfo}>
          <p className={classes.LotteryName}>
            {subscription.lottery_name ?? 'Lottery'}
          </p>
          <p className={classes.MetaText}>{categoryLabel}</p>
        </div>

        <button
          type="button"
          className={
            classes.ArrowButton +
            ' ' +
            (isExpanded ? classes.ArrowExpanded : classes.ArrowCollapsed)
          }
          aria-label="Toggle"
        >
          <ArrowIcon />
        </button>
      </div>

      <div className={classes.DatesRow}>
        {subscription.inserted_at && (
          <div>
            <p className={classes.MetaText + ' ' + classes.MetaTextSpaced}>
              Created:
            </p>
            <p className={classes.MetaValue}>
              {formatLottoDate(subscription.inserted_at).replace(' ', ' · ')}
            </p>
          </div>
        )}

        {subscription.draw_date && (
          <div>
            <p className={classes.MetaText + ' ' + classes.MetaTextSpaced}>
              Draw date:
            </p>
            <p className={classes.MetaValue}>
              {formatLottoDate(subscription.draw_date).replace(' ', ' · ')}
            </p>
          </div>
        )}
      </div>

      <div className={classes.Divider + ' ' + classes.DividerSpaced} />

      {subscription.balls?.length > 0 && (
        <>
          <div className={classes.BallsRow}>
            <BallsDisplay numbers={subscription.balls} size="small" />
          </div>
          <div className={classes.Divider} />
        </>
      )}

      <div className={classes.StakeOddsSection}>
        <div className={classes.StakeOddsCol}>
          <p className={classes.FooterLabel}>STAKE</p>
          <p className={classes.FooterValue}>
            {formatMoney(subscription.set_amount, 'ZAR')}
          </p>
        </div>
        <div className={classes.StakeOddsCol}>
          <p className={classes.FooterLabel}>ODDS</p>
          <p className={classes.FooterValue}>{formatOdds(subscription.odds)}</p>
        </div>
      </div>

      <div className={classes.PayoutSection}>
        <div>
          <p className={classes.FooterLabel}>BETS</p>
          <p className={classes.BetsCountValue}>{subscription.total_wagers}</p>
        </div>
        <div className={classes.PayoutRight}>
          <p className={classes.FooterLabel}>POTENTIAL PAYOUT</p>
          <p className={classes.PayoutValue}>
            {formatMoney(subscription.potential_payout, 'ZAR')}
          </p>
        </div>
      </div>

      <div
        className={classes.Collapse + ' ' + (isExpanded ? classes.Open : '')}
      >
        {sequenceBets.map((bet, i) => (
          <div key={bet.wager_set_ref ?? i} className={classes.WagerRow}>
            <p className={classes.WagerIndex}>
              BET {bet.sequence_number ?? i + 1}
            </p>
            <p className={classes.WagerStake}>
              {formatMoney(bet.set_amount, 'ZAR')}
            </p>
            <BetStatusBadge variant={bet.status} />
          </div>
        ))}

        <div className={classes.CancelBtnWrapper}>
          {cancelError && <p className={classes.ErrorText}>{cancelError}</p>}
          {onCancel && (
            <button
              type="button"
              className={classes.CancelButton}
              onClick={handleCancel}
              disabled={isCancelling}
            >
              {isCancelling ? '...' : 'Cancel'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionCard;
